import { FC, MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DescriptionIcon from '@mui/icons-material/Description';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Box,
  Collapse,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';

import { ReactComponent as TaskBlockedWarningIcon } from '@/assets/icons/task-blocked-warning-icon.svg';
import { TooltipWithIcon } from '@/shared/ui/TooltipWithIcon';

import {
  CardContainer,
  DetailsContainer,
  FileNameText,
  FileThumbnail,
  FileThumbnailPlaceholder,
  LabelText,
  UploaderBadge,
  ValueText,
} from './FileCard.styles';

export type FileCategory = 'Decoration' | 'Neutralization' | 'Artwork' | 'ArtworkPreview';

export interface FileData {
  id: number;
  category: FileCategory;
  thumbnail: string;
  slot?: 'before' | 'after';

  title: string;
  displayDate: string;
  realFileName: string;
  fileType: string;
  uploadDate: string;
  uploaderName: string;
  uploaderInitial: string;
  objectId?: string;
  canDelete?: boolean;
  deleteBlockedReason?: string;
}

interface FileCardProps {
  file: FileData;
  active: boolean;
  expanded: boolean;
  onSelect: (id: number) => void;
  onDelete?: (id: number) => void;
  hasDeleteOption?: boolean;
}

const FileCard: FC<FileCardProps> = ({
  file,
  active,
  expanded,
  onSelect,
  onDelete,
  hasDeleteOption = true,
}) => {
  const { t } = useTranslation();
  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);
  const [hasImageLoadError, setHasImageLoadError] = useState(false);

  useEffect(() => {
    setHasImageLoadError(false);
  }, [file.thumbnail]);

  const handleImageLoadError = () => {
    setHasImageLoadError(true);
  };

  const isVideo = /\.(mp4|webm|ogg|mov|avi)$/i.test(file.realFileName ?? '');
  const hasThumbnail = !isVideo && !!file.thumbnail && !hasImageLoadError;
  const isMenuOpen = Boolean(menuAnchorElement);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorElement(event.currentTarget);
  };

  const handleCloseMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorElement(null);
  };

  const handleDeleteFile = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onDelete?.(file.id);
    setMenuAnchorElement(null);
  };

  const handleToggleCardExpansion = () => {
    onSelect(file.id);
  };

  return (
    <CardContainer elevation={0} onClick={handleToggleCardExpansion} isActive={active}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap="0.75rem" alignItems="center" overflow="hidden" flex={1}>
          {isVideo ? (
            <FileThumbnailPlaceholder display="flex" alignItems="center" justifyContent="center">
              <VideocamIcon sx={{ color: '#9CA3AF', fontSize: '1.5rem' }} />
            </FileThumbnailPlaceholder>
          ) : hasThumbnail ? (
            <FileThumbnail
              src={file.thumbnail}
              onError={handleImageLoadError}
              alt={file.title}
              isPlaceholder={false}
            />
          ) : (
            <FileThumbnailPlaceholder />
          )}

          <Box display="flex" flexDirection="column" gap="0.25rem" minWidth={0}>
            <Tooltip title={file.title} placement="top">
              <FileNameText>{file.title}</FileNameText>
            </Tooltip>

            <Box display="flex" alignItems="center" gap="0.5rem">
              <UploaderBadge>{file.uploaderInitial}</UploaderBadge>
              <Typography sx={{ fontSize: '0.75rem', color: '#615E6F' }}>
                {file.displayDate}
              </Typography>
            </Box>
          </Box>
        </Box>

        {hasDeleteOption && (
          <IconButton size="small" onClick={handleOpenMenu} sx={{ color: '#615E6F' }}>
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <DetailsContainer>
          <LabelText>File Name:</LabelText>
          <Tooltip title={file.realFileName} placement="top">
            <ValueText textAlign="right" noWrap>
              {file.realFileName}
            </ValueText>
          </Tooltip>

          <LabelText>File Type:</LabelText>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap="4px">
            <DescriptionIcon
              sx={(theme) => ({ fontSize: '1rem', color: theme.palette.accent.main })}
            />
            <ValueText>{file.fileType}</ValueText>
          </Box>

          <LabelText>Upload Date:</LabelText>
          <ValueText textAlign="right">{file.uploadDate}</ValueText>

          <LabelText alignSelf="center">Uploaded by:</LabelText>
          <ValueText textAlign="right">{file.uploaderName}</ValueText>
        </DetailsContainer>
      </Collapse>

      <Menu
        anchorEl={menuAnchorElement}
        open={isMenuOpen}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: '0.5rem', mt: 0.5, minWidth: '120px' },
        }}
      >
        {onDelete &&
          (file.deleteBlockedReason ? (
            <TooltipWithIcon
              text={file.deleteBlockedReason}
              icon={<TaskBlockedWarningIcon />}
              placement="left"
              arrow
            >
              <span>
                <MenuItem disabled sx={{ color: '#615E6F', fontSize: '0.8rem' }}>
                  <ListItemIcon>
                    {t(isVideo ? 'buttons.delete_video' : 'buttons.delete_image')}
                  </ListItemIcon>
                </MenuItem>
              </span>
            </TooltipWithIcon>
          ) : (
            <MenuItem onClick={handleDeleteFile} sx={{ color: '#615E6F', fontSize: '0.8rem' }}>
              <ListItemIcon>
                {t(isVideo ? 'buttons.delete_video' : 'buttons.delete_image')}
              </ListItemIcon>
            </MenuItem>
          ))}
      </Menu>
    </CardContainer>
  );
};

export default FileCard;
